/// <reference path="../../polaris-web-components.d.ts" />
import React, { useEffect, useState } from 'react';
import { DeleteCharge, getShopUrl } from '../Services/CommonServices';
import { useNavigate } from 'react-router';

function Pricing({ chargeAlert, redirectToCharge, chargeBtnLoad, setChargeAlert, currentPlan, planLoading, refreshPlan }) {

    const navigate = useNavigate();
    var queryString = window.location.search;
    const storeUrl = getShopUrl();
    const [btnLoad, setBtnLoad] = useState(false);
    const [loadingPlan, setLoadingPlan] = useState(null); // Track which plan button is loading

    useEffect(() => {
        // Only refresh if we don't have current plan data and we're not already loading
        if (refreshPlan && storeUrl && !currentPlan && !planLoading) {
            const timeoutId = setTimeout(() => {
                refreshPlan();
            }, 100); // Small delay to debounce multiple rapid calls
            
            return () => clearTimeout(timeoutId);
        }
    }, [storeUrl]); // Simplified dependencies to prevent excessive calls

    // Debug logging
    useEffect(() => {
        console.log('Pricing Component - Current Plan Info:', {
            currentPlan,
            chargeAlert,
            freePlanActive: isPlanActive('free'),
            basicPlanActive: isPlanActive('basic'),
            proPlanActive: isPlanActive('pro')
        });
    }, [currentPlan, chargeAlert]);

    // Helper function to determine if a plan is active with optimistic detection
    const isPlanActive = (planName) => {
        if (currentPlan) {
            // If we have current plan data, use it to determine active plan
            const currentPlanName = currentPlan.toLowerCase();
            return currentPlanName === planName.toLowerCase();
        }
        
        // Optimistic fallback based on chargeAlert for immediate UI feedback
        if (planName.toLowerCase() === 'free') {
            return chargeAlert === true;
        } else if (planName.toLowerCase() === 'basic') {
            // If loading and no explicit plan data, make educated guess
            return chargeAlert === false && planLoading;
        } else if (planName.toLowerCase() === 'pro') {
            return false; // This will be determined by currentPlan when available
        }
        
        return false;
    };

    const deleteCharge = (storeUrl) => {
        setBtnLoad(true);
        setLoadingPlan('free');
        DeleteCharge(storeUrl)
            .then(res => {
                if (res) {
                    setChargeAlert(true)
                    setBtnLoad(false);
                    setLoadingPlan(null);
                    // Force refresh plan info after successful downgrade (bypass cache)
                    if (refreshPlan) {
                        refreshPlan(true); // Pass true to force cache refresh
                    }
                } else {
                    setBtnLoad(false);
                    setLoadingPlan(null);
                }
            })
            .catch(err => {
                console.error(`DeleteCharge error: ${JSON.stringify(err)}`);
                setBtnLoad(false);
                setLoadingPlan(null);
            });
    };

    const handleUpgrade = (plan) => {
        setLoadingPlan(plan);
        const planTier = plan === 'basic' ? 'Basic' : 'Pro';
        redirectToCharge(storeUrl, planTier);
        // Note: Plan refresh will happen when user returns from charge flow
        // and the app re-initializes
    };

    const PricingCard = ({
        title,
        description,
        price,
        features,
        isActive,
        buttonText,
        buttonAction,
        buttonLoading,
        buttonVariant = "primary",
        isLoading = false
    }) => {
        return (
            <div style={{
                position: 'relative',
                height: '100%',
                boxShadow: isActive ? '0px 0px 15px 4px #CDFEE1' : 'none',
                borderRadius: '0.75rem',
                opacity: isLoading ? 0.7 : 1,
                transition: 'all 0.3s ease-in-out'
            }}>
                {isActive && (
                    <div style={{ 
                        position: 'absolute', 
                        top: '-15px', 
                        right: '6px', 
                        zIndex: '100' 
                    }}>
                        <s-badge tone="success" size="large">
                            {isLoading ? 'Checking...' : 'Active Plan'}
                        </s-badge>
                    </div>
                )}
                
                <s-box blockSize="100%">
                    <s-section
                        background={isActive ? 'success-subdued' : 'surface'}
                        border={isActive ? 'success' : 'base'}
                        borderWidth={isActive ? 'large' : 'base'}
                        borderRadius="large"
                        minBlockSize="100%"
                    >
                        <s-stack gap="base">
                            <s-stack gap="small-100">
                                <s-heading>
                                    {title}
                                </s-heading>
                                {description && (
                                    <s-text color="subdued">
                                        {description}
                                    </s-text>
                                )}
                            </s-stack>

                            <s-stack direction="inline" gap="small-100" alignment="baseline">
                                <span style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>
                                    {price}
                                </span>
                                <s-text color="subdued">
                                    / month
                                </s-text>
                            </s-stack>

                            <s-stack gap="small-100">
                                {features?.map((feature, id) => (
                                    <s-text key={id}>
                                        • {feature}
                                    </s-text>
                                ))}
                            </s-stack>

                            <s-box paddingBlockStart="small">
                                <s-button
                                    variant={buttonVariant}
                                    onClick={buttonAction}
                                    loading={buttonLoading}
                                    disabled={isActive}
                                    inlineSize="100%"
                                >
                                    {buttonText}
                                </s-button>
                            </s-box>
                        </s-stack>
                    </s-section>
                </s-box>
            </div>
        );
    };

    return (
        <s-page heading="Choose your plan">
            <s-link slot="breadcrumb-actions" href={'/dashboard?' + queryString}>Dashboard</s-link>
            
            {/* Subtitle */}
            <s-box padding="none">
                <s-heading color="subdued">
                    Select the plan that best fits your business needs
                </s-heading>
                <br/>
            </s-box>

            {/* Pricing Cards Container */}
            <s-box padding="none">
                {/* Loading Overlay */}
                {planLoading && !currentPlan && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        borderRadius: '0.75rem'
                    }}>
                        <s-stack gap="base" alignment="center">
                            <s-spinner size="large" />
                            <s-text type="strong">Loading your plan information...</s-text>
                        </s-stack>
                    </div>
                )}
                
                <s-grid 
                    gridTemplateColumns="repeat(3, 1fr)" 
                    gap="large"
                    maxInlineSize="1200px"
                >
                            <s-grid-item>
                                <PricingCard
                                    title="Free"
                                    description="Perfect for getting started"
                                    price="$0"
                                    features={[
                                        "Manage up to 100 products",
                                        "Basic import & export",
                                        "CSV/XLSX file support",
                                        "Email support"
                                    ]}
                                    isActive={isPlanActive('free')}
                                    buttonText={isPlanActive('free') ? "Current Plan" : "Switch to Free"}
                                    buttonAction={() => deleteCharge(storeUrl)}
                                    buttonLoading={loadingPlan === 'free'}
                                    buttonVariant="secondary"
                                    isLoading={planLoading && !currentPlan}
                                />
                            </s-grid-item>

                            <s-grid-item>
                                <PricingCard
                                    title="Pro"
                                    description="For growing businesses"
                                    price="$19"
                                    features={[
                                        "Unlimited products",
                                        "Advanced import & export",
                                        "Schedule imports",
                                        "Priority support"
                                    ]}
                                    isActive={isPlanActive('basic')}
                                    buttonText={isPlanActive('basic') ? "Current Plan" : "Upgrade to Pro"}
                                    buttonAction={() => handleUpgrade('basic')}
                                    buttonLoading={loadingPlan === 'basic'}
                                    buttonVariant="primary"
                                    isLoading={planLoading && !currentPlan}
                                />
                            </s-grid-item>

                            <s-grid-item>
                                <PricingCard
                                    title="Advanced"
                                    description="Advanced users with backup & restore"
                                    price="$39"
                                    features={[
                                        "Everything in Pro",
                                        "Product backups & restore",
                                        "One-click restore",
                                        "Dedicated support"
                                    ]}
                                    isActive={isPlanActive('pro')}
                                    buttonText={isPlanActive('pro') ? "Current Plan" : "Upgrade to Advanced"}
                                    buttonAction={() => handleUpgrade('pro')}
                                    buttonLoading={loadingPlan === 'pro'}
                                    buttonVariant="primary"
                                    isLoading={planLoading && !currentPlan}
                                />
                            </s-grid-item>
                        </s-grid>
                    </s-box>

                    {/* FAQ Section */}
                    <br />
                        <s-section>
                            <s-stack gap="large">
                                <s-box textAlign="center">
                                    <s-heading>
                                        Frequently Asked Questions
                                    </s-heading>
                                </s-box>
                                
                                <s-divider />
                                
                                <s-stack gap="large">
                                    <s-stack gap="small">
                                        <s-heading>
                                            Can I switch plans anytime?
                                        </s-heading>
                                        <s-text color="subdued">
                                            Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
                                        </s-text>
                                    </s-stack>
                                    
                                    <s-stack gap="small">
                                        <s-heading>
                                            What happens if I exceed 100 products on the free plan?
                                        </s-heading>
                                        <s-text color="subdued">
                                            You'll need to upgrade to the Pro or Advanced plan to manage more than 100 products and access advanced features.
                                        </s-text>
                                    </s-stack>
                                    
                                    <s-stack gap="small">
                                        <s-heading>
                                            What's the difference between Basic and Pro plans?
                                        </s-heading>
                                        <s-text color="subdued">
                                            Advanced plan ($39/month) includes everything in Pro plus automatic product backups and one-click restore functionality to protect your data.
                                        </s-text>
                                    </s-stack>
                                    
                                    <s-stack gap="small">
                                        <s-heading>
                                            Is there a free trial for the Pro plan?
                                        </s-heading>
                                        <s-text color="subdued">
                                            The Free plan serves as a trial. Upgrade to Basic or Pro when you're ready for unlimited products and advanced features.
                                        </s-text>
                                    </s-stack>
                                </s-stack>
                            </s-stack>
                        </s-section>
        </s-page>
    );
}

export default Pricing;
